import { useEffect, useRef, useState } from 'react';
import { Shapes } from 'lucide-react';

import { IskToolbarThumb } from './IskToolbarThumb';

type Preview = {
  imageUrl?: string | null;
  imageOssId?: string | null;
};

type Props = {
  previews?: Preview[] | null;
  /** 单张正方形边长；列表默认 */
  size?: number;
  altPrefix?: string;
  className?: string;
  /**
   * 卡片：按父容器宽度撑满，正方形等比放大（多张仍两张半裁切）。
   */
  fillWidth?: boolean;
};

function visibleSlotCount(count: number): number {
  if (count >= 3) return 2.5;
  return Math.max(1, count);
}

/**
 * 列表固定边长 / 卡片按宽撑满：正方形缩略，flex 横排，第三张露一半。
 */
export function IskPreviewStack({
  previews,
  size = 80,
  altPrefix = '直觉草图',
  className = '',
  fillWidth = false,
}: Props) {
  const items = (previews || [])
    .filter((item) => String(item.imageUrl || '').trim() || String(item.imageOssId || '').trim())
    .slice(0, 3);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [hostWidth, setHostWidth] = useState(0);

  useEffect(() => {
    if (!fillWidth) return undefined;
    const el = hostRef.current;
    if (!el) return undefined;
    const update = () => setHostWidth(Math.round(el.clientWidth));
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [fillWidth]);

  if (!items.length) return null;

  const gap = 6;
  const slots = visibleSlotCount(items.length);

  if (fillWidth) {
    const side = hostWidth > 0
      ? Math.max(48, Math.round((hostWidth - gap * Math.max(0, Math.ceil(slots) - 1)) / slots))
      : 0;
    return (
      <div ref={hostRef} className={`w-full overflow-hidden ${className}`} aria-label={`${items.length} 张直觉草图`}>
        {side > 0 ? (
          <div className="flex" style={{ height: side, gap }}>
            {items.map((item, index) => (
              <IskToolbarThumb
                key={`${item.imageOssId || item.imageUrl}-${index}`}
                imageUrl={item.imageUrl}
                imageOssId={item.imageOssId}
                alt={`${altPrefix} ${index + 1}`}
                size={side}
                className="!rounded-none border-0"
              />
            ))}
          </div>
        ) : (
          <div className="aspect-square w-full bg-[#EDF1F7]" />
        )}
      </div>
    );
  }

  const side = Math.max(40, Math.round(size));
  const width = Math.round(side * slots + gap * Math.max(0, Math.ceil(slots) - 1));

  return (
    <div
      className={`relative flex shrink-0 overflow-hidden ${className}`}
      style={{ width, height: side }}
      aria-label={`${items.length} 张直觉草图`}
    >
      <div className="flex h-full items-stretch" style={{ gap }}>
        {items.map((item, index) => (
          <IskToolbarThumb
            key={`${item.imageOssId || item.imageUrl}-${index}`}
            imageUrl={item.imageUrl}
            imageOssId={item.imageOssId}
            alt={`${altPrefix} ${index + 1}`}
            size={side}
          />
        ))}
      </div>
    </div>
  );
}

export function IskPreviewStackEmpty({
  label = '暂无直觉草图',
  className = '',
  fillWidth = false,
}: {
  label?: string;
  className?: string;
  fillWidth?: boolean;
}) {
  return (
    <div
      className={
        `flex flex-col items-center justify-center gap-2 text-muted `
        + (fillWidth ? 'aspect-square w-full ' : 'min-h-16 min-w-16 px-3 py-4 ')
        + className
      }
    >
      <Shapes size={fillWidth ? 36 : 22} strokeWidth={1.5} className="opacity-55" aria-hidden />
      <p className="text-xs">{label}</p>
    </div>
  );
}
